import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, ArrowRight, Truck, BadgeCheck, Building2, RotateCcw } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { TRUCK_IMAGE } from "@/lib/faro";

const ITEMS = [
  {
    icon: Truck,
    title: "CAP Inicial",
    text: "Mercancías, viajeros o ambas a la vez. La formación obligatoria para empezar como conductor profesional.",
    tagline: "Mercancías · Viajeros · Ambas",
    bullets: [
      "**130 horas** teóricas",
      "**4 horas** en pista + **1 examen** de pista",
      "**6 horas** en ciudad + **1 examen** de ciudad",
    ],
  },
  {
    icon: BadgeCheck,
    title: "CAP Renovación",
    text: "Renueva tu certificado cada 5 años y mantén tu CAP siempre en vigor.",
    tagline: "Sin examen",
    bullets: [
      "**35 horas** teóricas",
      "**Sin examen**",
      "**Aparcamiento gratuito**",
    ],
  },
  {
    icon: ArrowRight,
    title: "CAP Puente o ampliación",
    text: "Para conductores que amplían de mercancías a viajeros (o viceversa) o con experiencia previa reconocida.",
    tagline: "Ampliación mercancías ↔ viajeros",
    bullets: [
      "**32,5 horas** teóricas",
      "**2,5 horas** prácticas",
      "**Examen** de 25 preguntas",
    ],
  },
  {
    icon: Building2,
    title: "Formación bonificada",
    text: "Cursos para empresas y trabajadores en activo con la bonificación gestionada por nosotros.",
    tagline: "Fundae · Sin coste",
    bullets: [
      "Gestionamos la **bonificación con Fundae**",
      "**Formación gratuita** para trabajador y empresa",
    ],
  },
];

const CapCardFront = ({ item, idx, onFlip }) => {
  const Icon = item.icon;
  return (
    <>
        {/* FRONT */}
        <button
          type="button"
          onClick={onFlip}
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", position: "relative" }}
          className="glass-card rounded-2xl p-5 text-white bg-white/5 border-white/15 h-full min-h-[240px] w-full text-left flex flex-col hover:bg-white/10 transition-colors cursor-pointer"
          data-testid={`cap-item-front-${idx}`}
        >
          <div className="w-10 h-10 rounded-xl bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] flex items-center justify-center">
            <Icon size={20} />
          </div>
          <h3 className="font-display font-bold text-lg mt-3">{item.title}</h3>
          <p className="text-sm text-white/80 mt-1 flex-1">{item.text}</p>
          <div className="mt-4 pt-3 border-t border-white/15 flex items-center justify-between text-xs font-bold text-[var(--faro-yellow)]">
            <span className="uppercase tracking-wider">{item.tagline}</span>{" "}
            <span className="inline-flex items-center gap-1">
              Ver detalle <ArrowRight size={12} />
            </span>
          </div>
        </button>
    </>
  );
};

const CapCardBack = ({ item, idx, onFlip }) => (
  <>
        {/* BACK */}
        <div
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            position: "absolute",
            inset: 0,
          }}
          className="rounded-2xl p-5 bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] border border-[var(--faro-yellow)] flex flex-col"
          data-testid={`cap-item-back-${idx}`}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-black opacity-80">Incluye</div>
              <h3 className="font-display font-black text-lg mt-0.5 leading-tight">{item.title}</h3>
            </div>
            <button
              type="button"
              onClick={onFlip}
              data-testid={`cap-item-back-close-${idx}`}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-[var(--faro-blue-deep)]/10 hover:bg-[var(--faro-blue-deep)]/20 transition-colors shrink-0"
              aria-label="Volver"
            >
              <RotateCcw size={16} />
            </button>
          </div>

          <ul className="mt-2 space-y-2 flex-1">
            {item.bullets.map((b) => {
              const parts = b.split(/\*\*(.+?)\*\*/g);
              return (
                <li key={b} className="text-sm flex items-start gap-2">
                  <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[var(--faro-red)] shrink-0" />
                  <span>
                    {parts.map((seg, i) =>
                      i % 2 === 1 ? (
                        <strong key={i} className="font-black">{seg}</strong>
                      ) : (
                        <React.Fragment key={i}>{seg}</React.Fragment>
                      )
                    )}
                  </span>
                </li>
              );
            })}
          </ul>

          <Link
            to="/curso-cap-vigo"
            data-testid={`cap-item-cta-${idx}`}
            className="mt-4 inline-flex items-center justify-center gap-2 bg-[var(--faro-blue-deep)] text-white font-black uppercase tracking-wider text-xs py-2.5 rounded-full hover:bg-[var(--faro-red)] transition-colors"
          >
            Infórmate <ArrowRight size={14} />
          </Link>
        </div>
  </>
);

function CapCard({ item, idx }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.35, delay: idx * 0.05 }}
      style={{ perspective: 1200 }}
      className="relative"
      data-testid={`cap-item-${idx}`}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformStyle: "preserve-3d", position: "relative", width: "100%" }}
        className="min-h-[240px] h-full"
      >
        <CapCardFront item={item} idx={idx} onFlip={() => setFlipped(true)} />

        <CapCardBack item={item} idx={idx} onFlip={() => setFlipped(false)} />

      </motion.div>
    </motion.div>
  );
}

export default function CapSection() {
  return (
    <section id="cap" className="bg-[var(--faro-blue)] text-white relative overflow-hidden" data-testid="cap-section">
      <div className="absolute inset-0 faro-noise opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-white/15">
            <img src={TRUCK_IMAGE} alt="Conductor profesional CAP" width="800" height="600" loading="lazy" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(19,26,95,0.7)] to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] rounded-2xl p-4">
              <div className="text-xs uppercase tracking-[0.2em] font-bold">Centros de Coruxo y A Cañiza</div>
              <div className="font-display font-black text-xl mt-1">Aparcamiento propio en Coruxo</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal>
            <span className="eyebrow text-[var(--faro-yellow)]">Formación profesional</span>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight flex items-center gap-3">
              <Award className="text-[var(--faro-yellow)] shrink-0" size={40} /> Cursos CAP
            </h2>
            <p className="mt-4 text-lg text-white/85 max-w-2xl">
              Somos referentes en la capacitación de conductores profesionales en la provincia de Pontevedra.
              Impartimos los cursos CAP en nuestros centros de <strong className="text-white">Coruxo y A Cañiza</strong>,
              con amplio aparcamiento propio en Coruxo para las prácticas.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
            {ITEMS.map((it, i) => (
              <CapCard key={it.title} item={it} idx={i} />
            ))}
          </div>

          <a href="#contacto" data-testid="cap-cta" className="btn-pill mt-8 bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] hover:brightness-95">
            Solicita información CAP <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
